import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import PromoBanner from "@/components/site/PromoBanner";
import SEO from "@/components/seo/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", image_url: "", caption: "", website: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/gallery`)
      .then((r) => !cancelled && setItems(r.data || []))
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.image_url || !form.caption) {
      toast.error("Please fill all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/gallery`, form);
      toast.success("Submitted! We review every entry — yours will appear once approved.");
      setForm({ name: "", email: "", image_url: "", caption: "", website: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="gallery-page" className="min-h-screen bg-[#F7F7F4] flex flex-col">
      <SEO title="Gallery" description="Things people have made with Vaymilo kits. Submit yours to be featured." path="/gallery" />
      <PromoBanner />
      <Header />

      <section className="bg-[#F7F7F4] border-b border-[#E7E7E2]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12">
          <div className="overline">— Made with Vaymilo</div>
          <h1 className="mt-5 font-sora text-[44px] sm:text-[60px] lg:text-[72px] font-extrabold tracking-tight leading-[1.02] text-[#111111]">Gallery.</h1>
          <p className="mt-6 max-w-2xl font-manrope text-[17px] md:text-[19px] text-[#111111]/70">Things people have shipped using a Vaymilo kit. Real work from real people.</p>
        </div>
      </section>

      <section className="flex-1 bg-white border-b border-[#E7E7E2]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          {loading && (
            <div className="font-mono text-sm text-[#111111]/50 tracking-widest uppercase">Loading…</div>
          )}
          {!loading && items.length === 0 && (
            <div data-testid="gallery-empty" className="font-manrope text-[#111111]/65 py-12">
              No entries yet. Be the first to submit yours below.
            </div>
          )}
          {!loading && items.length > 0 && (
            <div data-testid="gallery-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E7E7E2] border border-[#E7E7E2] rounded-2xl overflow-hidden">
              {items.map((it) => (
                <figure key={it.id} className="bg-white p-5">
                  <img
                    src={it.image_url}
                    alt={it.caption}
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover rounded-lg bg-[#F7F7F4]"
                  />
                  <figcaption className="mt-4 font-manrope text-[15px] text-[#111111]">
                    <p className="leading-relaxed">{it.caption}</p>
                    <p className="mt-2 text-[13px] text-[#111111]/60">— {it.name}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-[#F7F7F4]">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="overline">— Submit yours</div>
          <h2 className="mt-5 font-sora text-[32px] md:text-[40px] font-bold text-[#111111] tracking-tight">Made something with a kit?</h2>
          <p className="mt-4 font-manrope text-[16px] text-[#111111]/65">Share an image URL + a one-line caption. We review every entry before publishing.</p>

          <form onSubmit={submit} className="mt-8 bg-white border border-[#E7E7E2] rounded-2xl p-7 md:p-8 space-y-5">
            <div className="vaymilo-honeypot" aria-hidden="true">
              <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({...form, website: e.target.value})} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <input data-testid="gallery-name" required placeholder="Your name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                className="bg-white border border-[#E7E7E2] rounded-lg px-4 py-3 text-[15px] focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20" />
              <input data-testid="gallery-email" type="email" required placeholder="Email (kept private)" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                className="bg-white border border-[#E7E7E2] rounded-lg px-4 py-3 text-[15px] focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20" />
            </div>
            <input data-testid="gallery-image-url" type="url" required placeholder="https://… image URL" value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})}
              className="w-full bg-white border border-[#E7E7E2] rounded-lg px-4 py-3 text-[15px] focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20" />
            <textarea data-testid="gallery-caption" required rows={3} maxLength={300} placeholder="One-line caption (max 300 chars)" value={form.caption} onChange={(e) => setForm({...form, caption: e.target.value})}
              className="w-full bg-white border border-[#E7E7E2] rounded-lg px-4 py-3 text-[15px] focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 resize-none" />
            <button type="submit" disabled={submitting} data-testid="gallery-submit"
              className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white font-manrope font-medium text-[15px] px-6 py-3 rounded-lg transition-colors">
              {submitting ? "Submitting…" : "Submit for review"}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Gallery;
